<template lang="html">
<div class="selected-licence">

    <div class="loading" v-if="!queryDone">
        <p>Loading licence data...</p>
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>

    <div class="licence" v-if="queryDone">

        <h3 class="headline licence-name primary--text" v-text="queryData.name"></h3>

        <p class="body-2 mb-1 network-info">
            {{ licenceTerm }} - {{ licenceType }}<br>
            {{ brandingOrganization }}
        </p>

        <hr class="my-3">

        <div
        v-for="{ title, value } in properties"
        v-if="value"
        class="properties-item mb-3">
            <h4 class="subheading field-title grey--text" v-text="title"></h4>
            <div class="body-1 field-value" v-text="value"></div>
        </div>

    </div>
</div>
</template>

<script>
import gql from 'graphql-tag';
import moment from 'moment';
import sentenceCase from 'to-sentence-case';

import * as actions from '@/constants/actions';
import * as fieldTranslate from '@/functions/licenceFieldTranslate';
import { apolloClient } from '@/apollo/apollo';

export default {
    name: 'selected-licence',
    data() {
        return {
            queryData: null,
            queryDone: false,
            dataQuery: gql`query licenceDetails($id: String!) {
                getLicence(id: $id) {
                    name
                    term
                    type
                    net
                    nid
                    pod
                    src
                    activity
                    status
                    effdate
                    pdate
                    expdate
                    quantity_display
                    flag
                    hc
                    wtn
                }
            }`,
            properties: [],
        };
    },
    computed: {
        licence() {
            return this.$store.state.map.selectedLicence;
        },
        licenceId() {
            if (this.licence) {
                return this.licence.id;
            }
            return null;
        },
        dataQueryVars() {
            return {
                id: this.licenceId,
            };
        },
        // expand "GW" / "SW"
        licenceType() {
            const type = this.queryData.type || '';
            return fieldTranslate.licenceType(type);
        },
        // "water licence" / "use approval"
        licenceTerm() {
            const term = this.queryData.term || '';
            return fieldTranslate.licenceTermType(term);
        },
        // convert branding organization string to acronym
        brandingOrganization() {
            const org = this.queryData.net || '';
            return fieldTranslate.brandingOrganization(org);
        },
    },
    methods: {
        /**
        * handle initial data fetching and potentially licence changes
        */
        async initialize() {
            // reset local vars
            this.queryDone = false;
            this.queryData = null;

            // fetch data
            await this.getData();
        },
        /**
        * query the API and fetch station data
        */
        async getData() {
            this.$store.dispatch(actions.ADD_LOADING, 'load station data');
            try {
                const { data } = await apolloClient.query({
                    query: this.dataQuery,
                    variables: this.dataQueryVars,
                });

                // populate variables
                this.queryData = data.getLicence;

                this.queryDone = true;
                this.buildProps();
            } catch (ex) {
                this.$store.dispatch(actions.TRIGGER_TOAST, 'Error retrieving licences');
            }
            this.$store.dispatch(actions.REMOVE_LOADING, 'load station data');
        },
        /**
         * build an array of properties to display
         */
        buildProps() {
            const properties = [];
            const data = this.queryData;
            const displayDateFormat = 'MMMM Do, YYYY';
            // const numFormat = this.$options.filters.numFormat;

            // licence number and/or POD might be null
            if (data.nid || data.pod) {
                const licenceNumber = data.nid;
                const pod = data.pod;
                const titleArray = [];
                const valueArray = [];

                if (licenceNumber) {
                    titleArray.push('Licence Number');
                    valueArray.push(licenceNumber);
                }

                if (pod) {
                    titleArray.push('POD');
                    valueArray.push(pod);
                }

                const title = titleArray.join(' / ');
                const value = valueArray.join(' / ');

                properties.push({
                    title,
                    value,
                });
            }

            properties.push({
                title: 'Source',
                value: data.src,
            });

            properties.push({
                title: 'Purpose',
                value: data.activity,
            });

            if (data.status) {
                let value = sentenceCase(data.status);
                if (data.effdate) {
                    const date = moment(data.effdate).format(displayDateFormat);
                    value += ` (${date})`;
                }
                properties.push({
                    title: 'Status',
                    value,
                });
            }

            if (data.pdate) {
                const formatted = moment(data.pdate).format(displayDateFormat);
                properties.push({
                    title: 'Priority Date',
                    value: `${formatted}`,
                });
            }

            if (data.expdate) {
                const formatted = moment(data.expdate).format(displayDateFormat);
                properties.push({
                    title: 'Expiry Date',
                    value: `${formatted}`,
                });
            }

            properties.push({
                title: 'Quantity',
                value: data.quantity_display,
            });

            properties.push({
                title: 'Flag',
                value: data.flag,
            });

            properties.push({
                title: 'Hydraulic Connectivity',
                value: data.hc,
            });

            properties.push({
                title: 'Well Tag Number',
                value: data.wtn,
            });

            this.properties = properties;
        },
    },
    mounted() {
        this.initialize();
    },
    watch: {
        licenceId() {
            // re-initialize component when a new licence is selected
            this.initialize();
        },
    },
};
</script>

<style lang="scss">
.selected-licence {
    h3.licence-name {
        clear: both;
    }

    .properties-item {
        .field-title {
            font-weight: 500;
        }
    }

    .loading {
        height: 40vh;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
    }
}
</style>



// WEBPACK FOOTER //
// src/components/SidebarLicence.vue